import { useCallback, useContext, useRef } from 'react';
import type {
  StripeElements,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementShippingAddressChangeEvent,
} from '@stripe/stripe-js';
import { EXPRESS_FREE_SHIPPING_RATES } from '@/constants/stripe';
import { CheckoutContext } from '@/context/CheckoutContext';
import { shouldUseWalletDerivedZipTax } from '@/utils/tax';
import { getCurrentCheckoutZipCode } from '@/utils/zipCode';
import { logError, logInfo, logWarn } from '@/utils/logger';
import { ErrorLogs, InfoLogs } from '@/constants/logs';
import { WalletZipTaxStatus } from '@/types/walletZipTax';
import { useWalletZipTaxCalculator } from './useWalletZipTaxCalculator';

/**
 * ACDEV-25067: recalculates the checkout total from the postal code Stripe's
 * ExpressCheckoutElement returns on `shippingaddresschange`. With
 * `shippingAddressRequired` set, Stripe fires this as the Apple/Google Pay
 * sheet opens (for a default address) and on every address change, handing us
 * a redacted address (city/state/postal_code/country).
 *
 * We hit the tax endpoint via `useWalletZipTaxCalculator`, stash the result on
 * `capturedWalletTaxRef` for the `onConfirm` handler to forward to finalize, and update
 * the sheet in two complementary ways:
 *   - `elements.update({ amount })` moves the sheet's displayed Total (the
 *     only channel Stripe honors for the top-line number).
 *   - `event.resolve({ lineItems: [Subtotal, Tax] })` renders the itemized
 *     breakdown underneath the Total so the buyer sees why the number moved.
 * `lineItems` alone doesn't reliably move the Total across all ECE hosts, so
 * we keep the `elements.update` workaround as belt-and-suspenders. Sum of
 * `lineItems` matches `amount` — we compute both from the same breakdown.
 *
 * We deliberately do NOT write to CheckoutContext: the parent `<Elements>`
 * wrapper reads `amount` from `pricing`, so a context write would re-render
 * the wrapper and unmount the live wallet element mid-sheet.
 *
 * The handler MUST always `resolve` with valid shippingRates, otherwise the
 * sheet hangs on "loading" — so a missing/invalid ZIP or a failed tax lookup
 * re-resolves with the current total (and no breakdown) instead of bailing.
 *
 * `logConfirmTaxState(event)` is the funnel's last hop: call it at the top of
 * `onConfirm` so RUM records what finalize is about to carry (captured wallet
 * tax or not, live checkout tax validity, how many shippingaddresschange
 * events preceded the confirm, and the postal code/country Stripe put on the
 * confirm event's billing/shipping details). No-op when the wallet-tax gate is
 * closed for this session.
 */
export const useStripeWalletShippingZipTax = (elements: StripeElements | null) => {
  const { customer, pricing, addressDetails, featureFlags, sessionStartedWithoutValidTax } =
    useContext(CheckoutContext);
  const { runWalletZipTax, capturedWalletTaxRef, applyWalletTaxToCheckout } = useWalletZipTaxCalculator();
  // Pair the callback's presence with the shipping-config gate
  // (`getStripeExpressCheckoutShippingConfig`): non-US/CA sessions OR FF-OFF
  // sessions that also started with a valid tax (ACDEV-27914) get NEITHER
  // `shippingAddressRequired` NOR `onShippingAddressChange`, so Stripe never
  // installs a shippingaddresschange listener and no closure is retained for
  // an event that will never fire.
  const shouldCollectZipForTax = shouldUseWalletDerivedZipTax(customer, featureFlags, sessionStartedWithoutValidTax);
  // How many times Stripe actually delivered `shippingaddresschange` this
  // mount. Read at confirm so "the sheet never asked us for a total" is
  // distinguishable from "it asked and we produced nothing".
  const shippingAddressChangeCountRef = useRef(0);

  const onShippingAddressChange = useCallback(
    async (event: StripeExpressCheckoutElementShippingAddressChangeEvent): Promise<void> => {
      shippingAddressChangeCountRef.current += 1;

      // Always-resolve fallback path. Any early return / thrown error routes
      // through this so the sheet doesn't hang.
      const resolveWithCurrentTotal = () => event.resolve({ shippingRates: EXPRESS_FREE_SHIPPING_RATES });

      // Belt-and-suspenders outer try/catch: an unexpected throw from
      // `elements.update()`, `event.resolve()`, or the tax lookup itself
      // would otherwise leave the Apple/Google Pay sheet spinning forever.
      // Catch → log via RUM → resolve with the current total so the buyer
      // can still complete payment against the unchanged price. The address
      // log is inside this try for the same reason: a throw while reading the
      // wallet payload must still resolve the sheet.
      try {
        logInfo(InfoLogs.walletShippingAddressReceived, {
          provider: 'stripe',
          callCount: shippingAddressChangeCountRef.current,
          hasAddress: !!event.address,
          addressKeys: Object.keys(event.address ?? {}).join(','),
          hasPostalCode: !!event.address?.postal_code?.trim(),
          postalCode: event.address?.postal_code ?? '',
          country: event.address?.country?.toUpperCase() ?? '',
          state: event.address?.state ?? '',
        });

        const outcome = await runWalletZipTax(event.address?.postal_code, event.address?.country);

        // `runWalletZipTax` already logs every outcome to RUM
        // (`walletZipTaxOutcome` / `walletZipTaxError`) — no double-logging here.

        if (outcome.status === WalletZipTaxStatus.SUCCESS) {
          const { priceBreakdown } = outcome.result;
          const subtotal = priceBreakdown.priceTotal - priceBreakdown.tax;
          const lineItems = [
            { name: 'Subtotal', amount: subtotal },
            { name: 'Tax', amount: priceBreakdown.tax },
          ];

          // Update the parent <Elements> amount BEFORE resolving, then defer
          // resolve() by a macrotask so the ECE reads the new amount when it
          // re-paints the sheet. Reversing the order (resolve → update) makes
          // the update a no-op against the currently open wallet sheet.
          elements?.update({ amount: priceBreakdown.priceTotal });
          setTimeout(() => event.resolve({ shippingRates: EXPRESS_FREE_SHIPPING_RATES, lineItems }));
          return;
        }

        // `same_zip`, `unchanged`, `skipped`, `invalid`, `error`: total didn't
        // move (or we couldn't compute one), so skip `elements.update` and just
        // resolve with the current total.
        resolveWithCurrentTotal();
      } catch (error) {
        logError(ErrorLogs.walletZipTaxHandlerError, error, {
          provider: 'stripe',
          walletCountry: event.address?.country?.toUpperCase() ?? '',
        });
        resolveWithCurrentTotal();
      }
    },
    [elements, runWalletZipTax]
  );

  const logConfirmTaxState = (event: StripeExpressCheckoutElementConfirmEvent): void => {
    if (!shouldCollectZipForTax) return;
    const captured = capturedWalletTaxRef.current;
    const checkoutTaxValid = pricing.priceBreakdown.isTaxValid === true;
    const payload = {
      provider: 'stripe',
      paymentMethod: event.expressPaymentType,
      hasCapturedWalletTax: !!captured,
      capturedZipCode: captured?.zipCode ?? '',
      capturedSource: captured?.source ?? '',
      checkoutTaxValid,
      checkoutZipCode: getCurrentCheckoutZipCode(addressDetails, customer),
      shippingAddressChangeCount: shippingAddressChangeCountRef.current,
      hasShippingAddress: !!event.shippingAddress,
      shippingPostalCode: event.shippingAddress?.address?.postal_code ?? '',
      shippingCountry: event.shippingAddress?.address?.country?.toUpperCase() ?? '',
      hasBillingAddress: !!event.billingDetails?.address,
      billingPostalCode: event.billingDetails?.address?.postal_code ?? '',
      billingCountry: event.billingDetails?.address?.country?.toUpperCase() ?? '',
    };
    // Nothing captured AND the checkout tax is invalid is the exact state
    // psps rejects (`financialParams.estimatedTaxCalculation` isNotEmpty).
    if (!captured && !checkoutTaxValid) {
      logWarn(InfoLogs.walletConfirmTaxState, payload);
      return;
    }
    logInfo(InfoLogs.walletConfirmTaxState, payload);
  };

  return {
    onShippingAddressChange: shouldCollectZipForTax ? onShippingAddressChange : undefined,
    capturedWalletTaxRef,
    applyWalletTaxToCheckout,
    logConfirmTaxState,
  };
};
